import { useEffect, useMemo } from "react";
import { format } from "date-fns";
import { describeProvenance } from "@/lib/provenance";
import { Sentry } from "@/lib/sentry";

interface RecordMetadataProps {
  createdAt?: string;
  updatedAt?: string;
  createdByName?: string | null;
  updatedByName?: string | null;
  /** created_via / updated_via (#981): the channel when no staff user wrote the row. */
  createdVia?: string | null;
  updatedVia?: string | null;
  /** Used only to tag the untraced-provenance log, e.g. "quote". */
  entity?: string;
  recordId?: string;
  className?: string;
}

const DATE_FORMAT = "MMM d, yyyy 'at' h:mm a";

export function RecordMetadata({
  createdAt,
  updatedAt,
  createdByName,
  updatedByName,
  createdVia,
  updatedVia,
  entity,
  recordId,
  className = "",
}: RecordMetadataProps) {
  const created = useMemo(
    () => describeProvenance({ name: createdByName, via: createdVia, at: createdAt, action: "created" }),
    [createdByName, createdVia, createdAt],
  );
  const updated = useMemo(
    () => describeProvenance({ name: updatedByName, via: updatedVia, at: updatedAt, action: "updated" }),
    [updatedByName, updatedVia, updatedAt],
  );
  const showCreated = Boolean(createdAt);
  const showUpdated = Boolean(updatedAt && updatedAt !== createdAt);

  // An untraced row is a bug in a write path, not a display state: count it so it gets fixed.
  useEffect(() => {
    const log = (field: "created" | "updated", via: string | null | undefined) =>
      Sentry.captureMessage("record_provenance_untraced", {
        level: "warning",
        tags: { entity: entity ?? "unspecified", field, via: via ?? "null" },
        extra: { recordId },
      });
    if (showCreated && created.untraced) log("created", createdVia);
    if (showUpdated && updated.untraced) log("updated", updatedVia);
  }, [created.untraced, updated.untraced, entity, recordId, createdVia, updatedVia, showCreated, showUpdated]);

  return (
    <div className={`text-xs text-muted-foreground space-y-0.5 ${className}`}>
      {showCreated && createdAt && (
        <div>
          {created.lead} <span className="font-medium">{created.subject}</span> on {format(new Date(createdAt), DATE_FORMAT)}
        </div>
      )}
      {showUpdated && updatedAt && (
        <div>
          {updated.lead} <span className="font-medium">{updated.subject}</span> on {format(new Date(updatedAt), DATE_FORMAT)}
        </div>
      )}
    </div>
  );
}
